import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tabs,
  Tab,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  LockOutlined as LockOutlinedIcon,
  Image as ImageIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Add as AddIcon,
  AccountBalance as AccountBalanceIcon,
  Percent as PercentIcon,
  LocalOffer as LocalOfferIcon,
  PriceChange as PriceChangeIcon,
  SwapHoriz as SwapHorizIcon,
  RefreshRounded as RefreshRoundedIcon,
  Description as DescriptionIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { FP, PageHeader } from 'components/financepro';
import { tienePermisosModulo, puedeAccionProductos } from 'lib/seguridad/permisosPorModulo';
import { useProductosMaterialContext } from 'context';
import ListadoProductos from './ListadoProductos';
import AsociacionCuentasContables from './AsociacionCuentasContables';
import AgregarDepartamento from './AgregarDepartamento';
import ImportarProductosExcel from './ImportarProductosExcel';
import ReglasDescuentos from './ReglasDescuentos';
import Promociones from './Promociones';
import MantenimientoListasPrecios from './MantenimientoListasPrecios';
import TransaccionesInventario from './TransaccionesInventario';
import TransferenciasInventario from './TransferenciasInventario';
import ReglasImpuestosVentas from './ReglasImpuestosVentas';
import ConteoFisicoInventario from './ConteoFisicoInventario';

/** Formulario principal (pestaña Formulario / collapse Productos en rutas). */
const PROGRAMA_FORMULARIO_PRODUCTOS = 'mantenimientoproductos';
/** Igual que el botón Editar en `ListadoProductos.jsx`: acción de alta/edición vía `gestionproductos`. */
const PROGRAMA_AGREGAR_DEPARTAMENTO = 'agregardepartamentos';
const LS_SUCURSAL_MATRICULA = 'sucursalmatricula';

/** Busca en la lista del API (sucursales) la entrada que coincide con `sucursalmatricula` en texto completo o por código. */
function findLocalidadEnLista(list, raw) {
  if (!raw || !list?.length) return null;
  const r = String(raw).trim();
  if (!r) return null;
  const byTexto = list.find((l) => l.texto === r);
  if (byTexto) return byTexto;
  const code = r.split('-')[0]?.trim();
  if (code) {
    return list.find((l) => String(l.id) === code) || null;
  }
  return null;
}

/** Valor por defecto del campo localidad según `sucursalmatricula` o, sin esa clave, almacén por defecto sobre la lista completa.
 *  Si `omitirMatriculaLs` es true, no se usa localStorage (p. ej. matrícula no encontrada en servidor).
 */
function resolveDefaultLocalidadTexto(localidadesList, company, opts = {}) {
  const omitirMatriculaLs = opts.omitirMatriculaLs === true;
  const raw =
    omitirMatriculaLs || typeof window === 'undefined'
      ? ''
      : String(window.localStorage.getItem(LS_SUCURSAL_MATRICULA) || '').trim();
  if (raw && localidadesList.length > 0) {
    const hit = findLocalidadEnLista(localidadesList, raw);
    if (hit) return hit.texto;
  }
  if (!raw && localidadesList.length > 0) {
    const defId = company?.almacenpordefecto;
    if (defId != null && defId !== '') {
      const m = localidadesList.find((l) => String(l.id) === String(defId));
      if (m) return m.texto;
    }
  }
  if (localidadesList.length === 1) {
    return localidadesList[0].texto;
  }
  return '';
}

/** Mismos estilos de campo que `Motores/informemotores.js` (INPUT_SX). */
const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

const INPUT_MULTILINE_SX = {
  ...INPUT_SX,
  '& .MuiOutlinedInput-root': {
    ...INPUT_SX['& .MuiOutlinedInput-root'],
    height: 'auto',
    minHeight: 56,
    alignItems: 'flex-start',
    py: 1,
  },
};

const outlineBtnSx = {
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const primaryBtnSx = {
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  boxShadow: 'none',
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
};

/** Icono inicial por índice de pestaña (alineado con tabsDisponibles.index). */
const PRODUCTOS_TAB_ICON_BY_INDEX = [
  EditIcon,
  SearchIcon,
  AccountBalanceIcon,
  InventoryIcon,
  PercentIcon,
  LocalOfferIcon,
  PriceChangeIcon,
  InventoryIcon,
  SwapHorizIcon,
];

function ProductosTabStartIcon({ tabIndex }) {
  const Cmp = PRODUCTOS_TAB_ICON_BY_INDEX[tabIndex] || InventoryIcon;
  return <Cmp sx={{ fontSize: 18 }} />;
}

const MantenimientoProductos = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, roleDetalles, usuarioLog, claveLog } = controller;

  const puedeAgregarDepartamento = tienePermisosModulo(roleDetalles, PROGRAMA_AGREGAR_DEPARTAMENTO);
  const puedeEliminarProducto = puedeAccionProductos(roleDetalles, PROGRAMA_FORMULARIO_PRODUCTOS, 'borrar');
  const prevEmpresaDbRef = useRef(null);
  /** Lista completa devuelta por el API (se filtra el Autocomplete a la sucursal de matrícula). */
  const localidadesCompletasRef = useRef([]);

  /** Localidad única según `sucursalmatricula` validada en backend (tabla sucursal); el combo queda bloqueado. */
  const [localidadFijadaMatricula, setLocalidadFijadaMatricula] = useState(null);
  /** true si hay texto en localStorage pero el servidor no encontró esa sucursal (no aplicar coincidencia solo en cliente). */
  const [matriculaRechazadaBackend, setMatriculaRechazadaBackend] = useState(false);

  // Estados del formular
  const [localidad, setLocalidad] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipoProducto, setTipoProducto] = useState('Producto');
  const [catProducto, setCatProducto] = useState('Materia Prima');
  const [unidadMedida, setUnidadMedida] = useState('UDS');
  const [costord, setCostord] = useState('');
  const [mayor, setMayor] = useState('');
  const [precio, setPrecio] = useState('');
  const [existenciainicial, setExistenciainicial] = useState('');
  const [cantcaja, setCantcaja] = useState('');
  const [cantminima, setCantminima] = useState('');
  const [cantmaxima, setCantmaxima] = useState('');
  const [itbis, setItbis] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [ubicacionInventario, setUbicacionInventario] = useState('');
  const [imagenProducto, setImagenProducto] = useState(null);
  const [imagenPreview, setImagenPreview] = useState(null);
  
  // Estados auxiliares
  const [localidades, setLocalidades] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [tablaInventario, setTablaInventario] = useState('');
  const [productoId, setProductoId] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const guardandoRef = useRef(false);
  const [eliminando, setEliminando] = useState(false);
  const eliminandoRef = useRef(false);
  const [dialogoBusqueda, setDialogoBusqueda] = useState(false);
  const [productosBusqueda, setProductosBusqueda] = useState([]);
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [tabActual, setTabActual] = useState(0);
  const [dialogoAgregarDepartamento, setDialogoAgregarDepartamento] = useState(false);
  const [dialogoImportar, setDialogoImportar] = useState(false);
  const [reglasImpuestos, setReglasImpuestos] = useState([]);

  // Función para verificar si un programa tiene permiso de lectura
  const verificarPermisoLectura = useCallback(
    (programa) => tienePermisosModulo(roleDetalles, programa),
    [roleDetalles],
  );

  // Definir las tabs disponibles con sus programas correspondientes
  const tabsDisponibles = useMemo(() => [
    { label: 'Formulario', programa: 'mantenimientoproductos', index: 0 },
    { label: 'Listado', programa: 'listadoproductos', index: 1 },
    { label: 'Cuentas Contables', programa: 'cuentascontables', index: 2 },
    { label: 'Impuestos', programa: 'reglasimpuestos', index: 3 },
    { label: 'Reglas de Descuentos', programa: 'reglasdescuentos', index: 4 },
    { label: 'Promociones', programa: 'promociones', index: 5 },
    { label: 'Listas de Precios', programa: 'listasprecios', index: 6 },
    { label: 'Entradas / salidas inventario', programa: 'transaccionesinventario', index: 7 },
    { label: 'Transferencias', programa: 'transferenciasinventario', index: 8 },
    { label: 'Conteo Físico de Inventario', programa: 'conteofisicoinventario', index: 9 },
  ], []);

  // Filtrar tabs según permisos de lectura
  const tabsConPermiso = useMemo(() => 
    tabsDisponibles.filter((tab) => verificarPermisoLectura(tab.programa)),
    [tabsDisponibles, verificarPermisoLectura]
  );

  // Ajustar el índice de la tab actual si la tab seleccionada no tiene permiso
  useEffect(() => {
    if (tabsConPermiso.length > 0) {
      const tabActualConPermiso = tabsConPermiso.find((tab) => tab.index === tabActual);
      if (!tabActualConPermiso) {
        // Si la tab actual no tiene permiso, cambiar a la primera tab disponible
        setTabActual(tabsConPermiso[0].index);
      }
    }
  }, [tabsConPermiso, tabActual]);

  // Cargar datos iniciales
  useEffect(() => {
    if (!selectedCompany?.db_empresa) return;
    //cargarTablaInventario();
    cargarLocalidades();
    cargarDepartamentos();
    cargarReglasImpuestos();
  }, [selectedCompany?.db_empresa]);

  // Localidad por defecto desde localStorage (`sucursalmatricula`) y opciones del Autocomplete filtradas desde el API.
  useEffect(() => {
    if (localidadFijadaMatricula) return;
    if (!selectedCompany?.db_empresa || modoEdicion || localidades.length === 0) return;
    const text = resolveDefaultLocalidadTexto(localidades, selectedCompany, {
      omitirMatriculaLs: matriculaRechazadaBackend,
    });
    if (!text) return;
    const emp = selectedCompany.db_empresa;
    const switched = prevEmpresaDbRef.current !== emp;
    prevEmpresaDbRef.current = emp;
    if (switched) {
      setLocalidad(text);
      return;
    }
    setLocalidad((prev) => (prev.trim() === '' ? text : prev));
  }, [localidades, selectedCompany, modoEdicion, localidadFijadaMatricula, matriculaRechazadaBackend]);

  // Efectos para habilitar/deshabilitar campos según tipo
  useEffect(() => {
    if (tipoProducto === 'Servicio') {
      setCantmaxima('0');
      setCantminima('0');
      setExistenciainicial('0');
      setCantcaja('0');
    }
  }, [tipoProducto]);

  const cargarTablaInventario = async () => {
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_obtenertablainventario', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          anomesfacturacion: selectedCompany.anomesfacturacion,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setTablaInventario(data.tabla_inventario || '');
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al cargar tabla de inventario');
      }
    } catch (error) {
      showError('Error de conexión al cargar tabla de inventario');
    }
  };

  const cargarLocalidades = async () => {
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const basePayload = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
      };
      const response = await fetch(servidor + '/api/productosbackend_obtenerlocalidades', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(basePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al cargar localidades');
        return;
      }

      const data = await response.json();
      const list = data.localidades || [];
      localidadesCompletasRef.current = list;

      const raw =
        typeof window !== 'undefined'
          ? String(window.localStorage.getItem(LS_SUCURSAL_MATRICULA) || '').trim()
          : '';

      if (!raw) {
        setLocalidadFijadaMatricula(null);
        setMatriculaRechazadaBackend(false);
        setLocalidades(list);
        return;
      }

      const resMat = await fetch(servidor + '/api/productosbackend_localidaddesucursalmatricula', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...basePayload,
          sucursal_matricula: raw,
        }),
      });

      const datosMat = await resMat.json().catch(() => ({}));
      if (resMat.ok && datosMat.localidad?.id != null && datosMat.localidad?.texto) {
        const loc = datosMat.localidad;
        const item = { id: String(loc.id), texto: String(loc.texto) };
        setLocalidadFijadaMatricula(item);
        setMatriculaRechazadaBackend(false);
        setLocalidades([item]);
        setLocalidad(item.texto);
        return;
      }

      setLocalidadFijadaMatricula(null);
      setMatriculaRechazadaBackend(true);
      showError(datosMat.Error || 'La sucursal de matrícula no existe en el catálogo. Revise sucursalmatricula');
      setLocalidades(list);
    } catch (error) {
      showError('Error de conexión al cargar localidades');
    }
  };

  const cargarDepartamentos = async () => {
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_obtenerdepartamentos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const departamentosCargados = data.departamentos || [];
        setDepartamentos(departamentosCargados);

        // Si hay departamentos y el campo está vacío, establecer el primero
        if (departamentosCargados.length > 0 && !departamento) {
          const primerDepartamento = departamentosCargados[0];
          const nombreDepartamento =
            primerDepartamento.nombre || primerDepartamento.texto || '';
          if (nombreDepartamento) {
            setDepartamento(nombreDepartamento);
          }
        }
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al cargar departamentos');
      }
    } catch (error) {
      showError('Error de conexión al cargar departamentos');
    }
  };

  const cargarReglasImpuestos = async () => {
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/facturabackend_obtenerreglasimpuestos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setReglasImpuestos(data.reglas || []);
      }
    } catch (error) {
      // No bloquear la pantalla si falla la carga de reglas
    }
  };

  const convertirAMayusculas = (texto) => {
    return texto.toUpperCase();
  };

  const handleDescripcionChange = (e) => {
    const valor = convertirAMayusculas(e.target.value);
    setDescripcion(valor);
  };

  const handleImagenChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagenPreview(reader.result);
        // Convertir a base64 para enviar al backend
        const base64 = reader.result.split(',')[1];
        setImagenProducto(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const validarFormulario = () => {
    if (!descripcion.trim()) {
      showError('La descripción es requerida');
      return false;
    }

    if (!localidad.trim()) {
      showError('La localidad no puede estar en blanco. Revise');
      return false;
    }

    if (!localidad.includes('-')) {
      showError('Dato Incorrecto en la localidad');
      return false;
    }

    if (!String(departamento || '').trim()) {
      showError(
        departamentos.length === 0
          ? 'No hay departamentos creados. Agregue uno antes de guardar el artículo'
          : 'Seleccione el departamento. Revise',
      );
      return false;
    }

    if (!String(itbis || '').trim()) {
      showError('Seleccione el impuesto. Revise');
      return false;
    }

    if (tipoProducto === 'Producto') {
      if (!cantmaxima.trim()) {
        showError('Cantidad Maxima es Incorrecta. Revise');
        return false;
      }

      if (!cantminima.trim()) {
        showError('Cantidad Minima es Incorrecta. Revise');
        return false;
      }

      if (!cantcaja.trim()) {
        showError('Cantidad en caja es Incorrecta. Revise');
        return false;
      }

      if (!existenciainicial.trim()) {
        showError('Existencia Inicial es Incorrecta. Revise');
        return false;
      }
    }

    if (!precio.trim()) {
      showError('Precio es Incorrecto. Revise');
      return false;
    }

    if (!mayor.trim()) {
      showError('Precio Por Mayor es Incorrecta. Revise');
      return false;
    }

    if (!costord.trim()) {
      showError('Costo RD es Incorrecta. Revise');
      return false;
    }

    if (observaciones.length > 600) {
      showError('Solamente puede escribir hasta 600 caracteres en las observaciones');
      return false;
    }

    if (ubicacionInventario.length > 100) {
      showError('La ubicación de inventario no puede exceder 100 caracteres');
      return false;
    }

    return true;
  };

  const limpiarFormulario = () => {
    const todas =
      localidadesCompletasRef.current.length > 0 ? localidadesCompletasRef.current : localidades;
    if (localidadFijadaMatricula) {
      setLocalidades([localidadFijadaMatricula]);
      setLocalidad(localidadFijadaMatricula.texto);
    } else {
      setLocalidades(todas);
      setLocalidad(
        resolveDefaultLocalidadTexto(todas, selectedCompany, {
          omitirMatriculaLs: matriculaRechazadaBackend,
        }),
      );
    }
    setDepartamento('');
    setDescripcion('');
    setTipoProducto('Producto');
    setCatProducto('Materia Prima');
    setUnidadMedida('UDS');
    setCostord('');
    setMayor('');
    setPrecio('');
    setExistenciainicial('');
    setCantcaja('');
    setCantminima('');
    setCantmaxima('');
    setItbis('');
    setObservaciones('');
    setUbicacionInventario('');
    setImagenProducto(null);
    setImagenPreview(null);
    setProductoId(null);
    setModoEdicion(false);
  };

  const handleGuardar = async () => {
    if (guardandoRef.current || eliminandoRef.current) return;
    if (!validarFormulario()) {
      return;
    }

    guardandoRef.current = true;
    setGuardando(true);
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_guardar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: 'productosnoserializados',
          localidad: localidad,
          departamento: departamento,
          productoserializado: false,
          codigoserializado: '',
          descripcion,
          tipoproducto: tipoProducto,
          catproducto: catProducto,
          unidadmedida: unidadMedida,
          costord,
          mayor,
          precio,
          existenciainicial,
          cantcaja,
          cantminima,
          cantmaxima,
          itbis,
          observaciones,
          ubicacioninventario: ubicacionInventario.trim(),
          foto_base64: imagenProducto,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Datos guardados exitosamente');
        limpiarFormulario();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al guardar los datos');
      }
    } catch (error) {
      showError(error.message || 'Error de conexión al guardar');
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  const handleModificar = async () => {
    if (guardandoRef.current || eliminandoRef.current) return;
    if (!productoId) {
      showError('No hay producto seleccionado para modificar');
      return;
    }

    if (!validarFormulario()) {
      return;
    }

    guardandoRef.current = true;
    setGuardando(true);
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_modificar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: "productosnoserializados",
          producto_id: productoId,
          localidad,
          departamento,
          productoserializado: false,
          codigoserializado: '',
          descripcion,
          tipoproducto: tipoProducto,
          catproducto: catProducto,
          unidadmedida: unidadMedida,
          costord,
          mayor,
          precio,
          existenciainicial,
          cantcaja,
          cantminima,
          cantmaxima,
          itbis,
          observaciones,
          ubicacioninventario: ubicacionInventario.trim(),
          foto_base64: imagenProducto,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Datos modificados exitosamente');
        limpiarFormulario();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al modificar los datos');
      }
    } catch (error) {
      showError('Error de conexión al modificar');
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  const handleEliminar = async () => {
    if (eliminandoRef.current || guardandoRef.current) return;
    if (!productoId) {
      showError('No hay producto seleccionado para eliminar');
      return;
    }

    if (!window.confirm('¿Está seguro que desea eliminar este producto?')) {
      return;
    }

    eliminandoRef.current = true;
    setEliminando(true);
    try {
      const codigo = productoId;

      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_eliminar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tablaInventario,
          codigo,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Producto eliminado exitosamente');
        limpiarFormulario();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al eliminar');
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
    } finally {
      eliminandoRef.current = false;
      setEliminando(false);
    }
  };

  const realizarBusqueda = async () => {
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_buscarproductos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: "productosnoserializados",
          descripcion: busquedaTexto,
          localidad: localStorage.getItem('localidad') || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setProductosBusqueda(data.productos || []);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al buscar');
      }
    } catch (error) {
      showError('Error de conexión al buscar');
    }
  };

  const seleccionarProducto = async (codigo) => {
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_obtenerproducto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: "productosnoserializados",
          codigo,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const producto = data.producto;
        
        // Llenar formulario
        setProductoId(producto.id);
        setDescripcion(producto.descripcion || '');
        setTipoProducto(producto.tipoproducto || 'Producto');
        setCatProducto(producto.catproducto || 'Materia Prima');
        setUnidadMedida(producto.medida || 'UDS');
        setCostord(producto.costo ? producto.costo.toFixed(2) : '');
        setMayor(producto.mayor ? producto.mayor.toFixed(2) : '');
        setPrecio(producto.precio ? producto.precio.toFixed(2) : '');
        setExistenciainicial(producto.inicial ? String(producto.inicial) : '');
        setCantcaja(producto.cantcaja ? String(producto.cantcaja) : '');
        setCantminima(producto.cantmin ? String(producto.cantmin) : '');
        setCantmaxima(producto.cantmax ? String(producto.cantmax) : '');
        setItbis(producto.itbis ? producto.nombreimpuesto + '-' + String(producto.itbis) : 'Exento-0%');
        setObservaciones(producto.garantia || '');
        setUbicacionInventario(producto.ubicacioninventario || '');
        setDepartamento(producto.departamento || '');

        // Localidad fijada por matrícula (backend); si no, mismo formato que catálogo: "id-descripción"
        if (localidadFijadaMatricula) {
          setLocalidad(localidadFijadaMatricula.texto);
          setLocalidades([localidadFijadaMatricula]);
        } else {
          const todasLoc =
            localidadesCompletasRef.current.length > 0
              ? localidadesCompletasRef.current
              : localidades;
          const rawLoc =
            producto.localidad != null && producto.localidad !== '' ? String(producto.localidad).trim() : '';
          const hit = rawLoc ? findLocalidadEnLista(todasLoc, rawLoc) : null;
          if (hit) {
            setLocalidad(hit.texto);
            setLocalidades((prev) =>
              prev.some((l) => String(l.id) === String(hit.id))
                ? prev
                : [...prev, { id: hit.id, texto: hit.texto }],
            );
          } else if (rawLoc) {
            setLocalidad(rawLoc);
          } else {
            setLocalidad('');
          }
        }
        
        // Buscar departamento
        if (producto.departamento) {
          const departamentoEncontrado = departamentos.find(d => 
            (d.id === String(producto.departamento)) || 
            (d.nombre || d.texto) === String(producto.departamento)
          );
          if (departamentoEncontrado) {
            setDepartamento(departamentoEncontrado.nombre || departamentoEncontrado.texto);
          } else {
            setDepartamento(String(producto.departamento));
          }
        } else {
          setDepartamento('');
        }

        // Imagen
        if (producto.foto_base64) {
          setImagenPreview(`data:image/png;base64,${producto.foto_base64}`);
          setImagenProducto(producto.foto_base64);
        }
        
        setModoEdicion(true);
        setDialogoBusqueda(false);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al obtener producto');
      }
    } catch (error) {
      showError('Error de conexión al obtener producto');
    }
  };

  const handleSeleccionarProductoDesdeListado = async (codigo) => {
    await seleccionarProducto(codigo);
    setTabActual(0);
  };

  const refrescarCatalogos = () => {
    if (!selectedCompany?.db_empresa) return;
    cargarTablaInventario();
    cargarLocalidades();
    cargarDepartamentos();
    cargarReglasImpuestos();
  };

  if (!selectedCompany?.db_empresa) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Mantenimiento de Productos" subtitle="Seleccione una empresa activa" showQuickActions={false} />
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 520,
              width: '100%',
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              p: { xs: 3, md: 4 },
              textAlign: 'center',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 48, color: FP.color.textMuted, mb: 2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 1 }}>
              Sin empresa seleccionada
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
              Elija una empresa en la barra superior para administrar productos e inventario.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Mantenimiento de Productos"
        subtitle={
          nombreNegocio && nombreNegocio !== 'false'
            ? `${nombreNegocio} · Inventario, precios y operaciones`
            : 'Gestión de inventario, precios y operaciones relacionadas'
        }
        cta={
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={refrescarCatalogos}
            sx={{
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              bgcolor: '#FFFFFF',
              borderRadius: 1.4,
              px: 2,
              height: 38,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Actualizar
          </Button>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {tabsConPermiso.length > 0 && (
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={tabsConPermiso.findIndex((tab) => tab.index === tabActual)}
              onChange={(e, newValue) => {
                const tabSeleccionada = tabsConPermiso[newValue];
                if (tabSeleccionada) {
                  setTabActual(tabSeleccionada.index);
                }
              }}
              sx={{
                minHeight: 48,
                borderBottom: `1px solid ${FP.color.border}`,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  color: FP.color.textSecondary,
                  minHeight: 48,
                  px: 2.4,
                },
                '& .MuiTab-root.Mui-selected': { color: FP.color.navy },
                '& .MuiTabs-indicator': { backgroundColor: FP.color.navy, height: 3 },
              }}
            >
              {tabsConPermiso.map((tab) => (
                <Tab
                  key={tab.index}
                  icon={<ProductosTabStartIcon tabIndex={tab.index} />}
                  iconPosition="start"
                  label={tab.label}
                />
              ))}
            </Tabs>
          </Card>
        )}

        {tabsConPermiso.length === 0 && (
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Card
              elevation={0}
              sx={{
                maxWidth: 560,
                width: '100%',
                borderRadius: 2.5,
                border: `1px solid ${FP.color.border}`,
                bgcolor: FP.color.cardBg,
                boxShadow: FP.shadow.soft,
                textAlign: 'center',
                p: { xs: 3, md: 4 },
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 1.6,
                  mx: 'auto',
                  mb: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: FP.color.dangerSoft,
                  color: FP.color.danger,
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.25rem',
                  color: FP.color.textPrimary,
                  mb: 1,
                  letterSpacing: '-0.01em',
                }}
              >
                Acceso Denegado
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
                No tiene permisos para acceder a ninguna sección de Mantenimiento de Productos.
              </Typography>
              <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
                Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
              </Typography>
            </Card>
          </Box>
        )}

        {tabActual === 0 && verificarPermisoLectura('mantenimientoproductos') && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <InventoryIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Datos del producto
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>
                {modoEdicion ? 'Modo edición' : 'Nuevo registro'}
              </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={3} alignItems="stretch">
            {/* Imagen del producto + localidad (columna que crece con el formulario) */}
            <Grid item xs={12} sm={3} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  borderRadius: 2.5,
                  bgcolor: FP.color.surfaceAlt,
                  border: `1px dashed ${FP.color.border}`,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: { xs: 180, sm: 200 },
                    width: '100%',
                    borderRadius: 1.6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: FP.color.surface,
                    mb: 2,
                    overflow: 'hidden',
                    border: `1px solid ${FP.color.border}`,
                  }}
                >
                  {imagenPreview ? (
                    <img
                      src={imagenPreview}
                      alt="Producto"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: 8,
                      }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: 64, color: FP.color.textMuted }} />
                  )}
                </Box>
                <Button
                  variant="outlined"
                  fullWidth
                  component="label"
                  startIcon={<ImageIcon />}
                  sx={{ ...outlineBtnSx, mb: 2 }}
                >
                  Seleccionar imagen
                  <input type="file" hidden accept="image/*" onChange={handleImagenChange} />
                </Button>
                <Autocomplete
                  options={localidades}
                  getOptionLabel={(option) => option.texto || ''}
                  value={localidades.find(l => l.texto === localidad) || null}
                  onChange={(e, newValue) => {
                    if (localidadFijadaMatricula) return;
                    setLocalidad(newValue ? newValue.texto : '');
                  }}
                  disabled={!!localidadFijadaMatricula}
                  disableClearable={!!localidadFijadaMatricula}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Localidad"
                      required
                      sx={INPUT_SX}
                      helperText={localidadFijadaMatricula ? 'Definida por la sucursal de matrícula (localStorage)' : ''}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 15,
                        readOnly: !!localidadFijadaMatricula,
                      }}
                    />
                  )}
                />
              </Paper>
            </Grid>

            {/* Campos principales */}
            <Grid item xs={12} sm={9}>
              <Grid container spacing={2.5}>
                {/* Categoría (campo API: departamento) */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete
                      options={departamentos}
                      getOptionLabel={(option) => option.nombre || option.texto || ''}
                      value={departamentos.find(d => 
                        (d.nombre || d.texto) === departamento
                      ) || null}
                      onChange={(e, newValue) => 
                        setDepartamento(newValue ? (newValue.nombre || newValue.texto) : '')
                      }
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField {...params} label="Categoría" sx={INPUT_SX} />
                      )}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setDialogoAgregarDepartamento(true)}
                      disabled={!puedeAgregarDepartamento}
                      sx={{
                        ...outlineBtnSx,
                        minWidth: 46,
                        width: 46,
                        px: 0,
                      }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>
                </Grid>

                {/* Descripción */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre del producto"
                    value={descripcion}
                    onChange={handleDescripcionChange}
                    required
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{ shrink: true }}
                    sx={INPUT_SX}
                  />
                </Grid>

                {/* Tipo y Categoría */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={INPUT_SX}>
                    <InputLabel id="tipo-prod-label">Tipo</InputLabel>
                    <Select
                      labelId="tipo-prod-label"
                      value={tipoProducto}
                      onChange={(e) => setTipoProducto(e.target.value)}
                      label="Tipo"
                    >
                      <MenuItem value="Producto">Producto</MenuItem>
                      <MenuItem value="Servicio">Servicio</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={INPUT_SX}>
                    <InputLabel id="cat-prod-label">Cat. producto</InputLabel>
                    <Select
                      labelId="cat-prod-label"
                      value={catProducto}
                      onChange={(e) => setCatProducto(e.target.value)}
                      label="Cat. producto"
                    >
                      <MenuItem value="Materia Prima">Materia Prima</MenuItem>
                      <MenuItem value="Producto Terminado">Producto Terminado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Divider con título */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Chip
                      label="Información de precios"
                      icon={<CategoryIcon sx={{ color: FP.color.navy }} />}
                      sx={{
                        bgcolor: FP.color.accentBlueSoft,
                        color: FP.color.navy,
                        fontWeight: 700,
                        border: `1px solid ${FP.color.border}`,
                      }}
                    />
                  </Divider>
                </Grid>

                {/* Unidad y Precios */}
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth sx={INPUT_SX}>
                    <InputLabel id="unidad-prod-label">Unidad</InputLabel>
                    <Select
                      labelId="unidad-prod-label"
                      value={unidadMedida}
                      onChange={(e) => setUnidadMedida(e.target.value)}
                      label="Unidad"
                    >
                      <MenuItem value="UDS">UDS</MenuItem>
                      <MenuItem value="KGS">KGS</MenuItem>
                      <MenuItem value="LBS">LBS</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Costo"
                    value={costord}
                    onChange={(e) => setCostord(e.target.value)}
                    required
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Mayor"
                    value={mayor}
                    onChange={(e) => setMayor(e.target.value)}
                    required
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Precio"
                    value={precio}
                    onChange={(e) => setPrecio(e.target.value)}
                    required
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={INPUT_SX}
                  />
                </Grid>

                {/* Divider con título */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Chip
                      label="Control de inventario"
                      icon={<InventoryIcon sx={{ color: FP.color.navy }} />}
                      sx={{
                        bgcolor: FP.color.accentBlueSoft,
                        color: FP.color.navy,
                        fontWeight: 700,
                        border: `1px solid ${FP.color.border}`,
                      }}
                    />
                  </Divider>
                </Grid>

                {/* Ubicación física en almacén */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ubicación de inventario"
                    value={ubicacionInventario}
                    onChange={(e) => setUbicacionInventario(e.target.value)}
                    placeholder="Ej. Pasillo A, Estante 3"
                    inputProps={{ maxLength: 100 }}
                    disabled={tipoProducto === 'Servicio'}
                    sx={INPUT_SX}
                  />
                </Grid>

                {/* Existencias */}
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Inicial"
                    value={existenciainicial}
                    onChange={(e) => setExistenciainicial(e.target.value)}
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    disabled={tipoProducto === 'Servicio'}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Cant. Caja"
                    value={cantcaja}
                    onChange={(e) => setCantcaja(e.target.value)}
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    disabled={tipoProducto === 'Servicio'}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Cant. Min"
                    value={cantminima}
                    onChange={(e) => setCantminima(e.target.value)}
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    disabled={tipoProducto === 'Servicio'}
                    required={tipoProducto === 'Producto'}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Cant. Max"
                    value={cantmaxima}
                    onChange={(e) => setCantmaxima(e.target.value)}
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    disabled={tipoProducto === 'Servicio'}
                    required={tipoProducto === 'Producto'}
                    sx={INPUT_SX}
                  />
                </Grid>

                {/* ITBIS */}
                <Grid item xs={12} sm={3}>
                  <FormControl
                    fullWidth
                    required
                    sx={INPUT_SX}
                  >
                    <InputLabel id="itbis-label">Impuesto</InputLabel>
                    <Select
                      labelId="itbis-label"
                      label="ITBIS"
                      value={itbis}
                      onChange={(e) => setItbis(e.target.value)}
                      renderValue={(v) => {
                        if (v === '' || v == null) return 'Exento-0';
                        const regla = reglasImpuestos.find(r => String(r.porcentaje) === String(v));
                        return regla ? `${regla.nombre}-${regla.porcentaje}%` : `${v}%`;
                      }}
                    >
                      {reglasImpuestos.map((regla) => (
                        <MenuItem key={regla.id} value={String(regla.nombre+'-'+regla.porcentaje)}>
                          {regla.nombre}-{regla.porcentaje}%
                        </MenuItem>
                      ))}
                      
                    </Select>
                  </FormControl>
                </Grid>

                {/* Observaciones */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observaciones"
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    multiline
                    rows={4}
                    inputProps={{ maxLength: 600 }}
                    sx={INPUT_MULTILINE_SX}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: FP.color.border }} />

          {/* Botones de acción */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.2,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              pt: 2.5,
              borderTop: `1px solid ${FP.color.border}`,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setDialogoBusqueda(true)}
              sx={outlineBtnSx}
            >
              Buscar
            </Button>
            <Button variant="outlined" startIcon={<ClearIcon />} onClick={limpiarFormulario} sx={outlineBtnSx}>
              Limpiar
            </Button>
            {tienePermisosModulo(roleDetalles, PROGRAMA_FORMULARIO_PRODUCTOS) && (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setDialogoImportar(true)}
                sx={outlineBtnSx}
              >
                Importar Excel
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={eliminando ? undefined : <DeleteIcon />}
              onClick={handleEliminar}
              disabled={!puedeEliminarProducto || eliminando || guardando}
              sx={{
                ...outlineBtnSx,
                borderColor: FP.color.danger,
                color: FP.color.danger,
                '&:hover': {
                  bgcolor: FP.color.dangerSoft,
                  borderColor: FP.color.danger,
                },
                '&.Mui-disabled': eliminando
                  ? { borderColor: FP.color.danger, color: FP.color.danger }
                  : { borderColor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              {eliminando ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  Eliminando...
                </Box>
              ) : (
                'Eliminar'
              )}
            </Button>
            <Button
              variant="contained"
              startIcon={guardando ? undefined : <SaveIcon />}
              onClick={modoEdicion ? handleModificar : handleGuardar}
              disabled={!tienePermisosModulo(roleDetalles, PROGRAMA_FORMULARIO_PRODUCTOS) || guardando || eliminando}
              sx={{
                ...primaryBtnSx,
                ...(guardando
                  ? { '&.Mui-disabled': { bgcolor: FP.color.navy, color: '#FFFFFF' } }
                  : {}),
              }}
            >
              {guardando ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  {modoEdicion ? 'Modificando...' : 'Guardando...'}
                </Box>
              ) : modoEdicion ? (
                'Modificar'
              ) : (
                'Guardar'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Diálogo de Búsqueda */}
      <Dialog
        open={dialogoBusqueda}
        onClose={() => setDialogoBusqueda(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
              Buscar producto
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDialogoBusqueda(false)}
            size="small"
            sx={{ color: FP.color.textSecondary }}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Box sx={{ mb: 1 }}>
            <Autocomplete
              options={productosBusqueda}
              getOptionLabel={(option) => option.texto || ''}
              inputValue={busquedaTexto}
              onInputChange={(e, newValue) => {
                setBusquedaTexto(newValue);
                if (newValue.length > 2) {
                  realizarBusqueda();
                }
              }}
              onChange={(e, newValue) => {
                if (newValue) {
                  seleccionarProducto(newValue.codigo);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar producto"
                  placeholder="Escriba para buscar…"
                  sx={INPUT_SX}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button onClick={() => setDialogoBusqueda(false)} sx={outlineBtnSx}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {tabActual === 1 && verificarPermisoLectura('listadoproductos') && (
        <ListadoProductos onSeleccionarProducto={handleSeleccionarProductoDesdeListado} />
      )}

      {tabActual === 2 && verificarPermisoLectura('cuentascontables') && (
        <AsociacionCuentasContables />
      )}

      {tabActual === 3 && verificarPermisoLectura('reglasimpuestos') && (
        <ReglasImpuestosVentas />
      )}

      {tabActual === 4 && verificarPermisoLectura('reglasdescuentos') && (
        <ReglasDescuentos />
      )}

      {tabActual === 5 && verificarPermisoLectura('promociones') && (
        <Promociones />
      )}

      {tabActual === 6 && verificarPermisoLectura('listasprecios') && (
        <MantenimientoListasPrecios />
      )}

      {tabActual === 7 && verificarPermisoLectura('transaccionesinventario') && (
        <TransaccionesInventario />
      )}

      {tabActual === 8 && verificarPermisoLectura('transferenciasinventario') && (
        <TransferenciasInventario />
      )}

      {tabActual === 9 && verificarPermisoLectura('conteofisicoinventario') && (
        <ConteoFisicoInventario />
      )}
      
      {/* Importación masiva de productos desde Excel */}
      <ImportarProductosExcel
        open={dialogoImportar}
        onClose={() => setDialogoImportar(false)}
        selectedCompany={selectedCompany}
        usuarioLog={usuarioLog}
        claveLog={claveLog}
        localidades={localidades}
        localidadInicial={localidad}
        localidadFijada={localidadFijadaMatricula}
        departamentos={departamentos}
        reglasImpuestos={reglasImpuestos}
        showSuccess={showSuccess}
        showError={showError}
      />

      {/* Diálogo para Agregar Departamento */}
      <AgregarDepartamento
        open={dialogoAgregarDepartamento}
        onClose={() => setDialogoAgregarDepartamento(false)}
        onGuardar={cargarDepartamentos}
        selectedCompany={selectedCompany}
        showSuccess={showSuccess}
        showError={showError}
      />
      </Box>
    </Box>
  );
};

export default MantenimientoProductos;
